import { useQuery } from '@tanstack/react-query';
import { api } from '@/services/api';

/**
 * Dettaglio storico di una selettiva (SOLO admin distaccamento). Apre una modal
 * con lo SNAPSHOT dei destinatari fotografato all'invio: per ciascuno mostra il
 * nominativo, la disponibilita' di allora, la risposta SI/NO e l'uso del GPS.
 * Per selettive storiche senza snapshot il backend fa fallback sulle risposte
 * (disponibilita' = "Non registrato").
 */

type AvailabilityState =
  | 'Available'
  | 'NotAvailable'
  | 'FromCalendarAvailable'
  | 'FromCalendarNotAvailable'
  | 'DoNotDisturb'
  | 'NonRegistrato';

/** Etichette IT centralizzate per lo stato disponibilita' (no magic string sparse). */
const AVAILABILITY_LABELS: Record<AvailabilityState, string> = {
  Available: 'Disponibile',
  NotAvailable: 'Non disponibile',
  FromCalendarAvailable: 'Disponibile da calendario',
  FromCalendarNotAvailable: 'Non disponibile da calendario',
  DoNotDisturb: 'Non disturbare',
  NonRegistrato: 'Non registrato',
};

function availabilityLabel(state: string): string {
  return AVAILABILITY_LABELS[state as AvailabilityState] ?? state;
}

/** Etichette IT per il target della selettiva. */
const TARGET_LABELS: Record<string, string> = {
  All: 'Tutti gli operativi',
  Available: 'Solo disponibili',
  Custom: 'Gruppo scelto',
};

interface DetailRecipient {
  userId: string;
  nickname: string;
  availabilityState: string;
  response: 'SI' | 'NO' | null;
  usedGps: boolean;
}

interface DetailMember {
  userId: string;
  nickname: string;
}

interface SelectiveDetail {
  target: string;
  customGroupMembers: DetailMember[] | null;
  recipients: DetailRecipient[];
  terminatedAt: string | null;
  terminatedByNickname: string | null;
  noLongerNeededAt: string | null;
  noLongerNeededByNickname: string | null;
}

function fmtDateTime(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('it-IT');
}

export function SelectiveDetailModal({ selectiveId, onClose }: {
  selectiveId: string;
  onClose: () => void;
}): React.JSX.Element {
  const { data, isLoading } = useQuery<SelectiveDetail>({
    queryKey: ['selective.detail', selectiveId],
    queryFn: async () => (await api.get(`/selectives/${selectiveId}/detail`)).data,
  });

  const target = data ? (TARGET_LABELS[data.target] ?? data.target) : '';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 700 }}>
        <h3 style={{ marginBottom: 16 }}>📋 Dettaglio selettiva</h3>

        {isLoading && <p style={{ textAlign: 'center' }}>Caricamento...</p>}

        {!isLoading && data && (
          <>
            <div style={{ marginBottom: 14 }}>
              <strong>Destinatari:</strong> {target}
            </div>

            {/* Info aggiuntive (Bryan #4): chi ha terminato e se/da chi e'
                stato inviato l'avviso "intervento non piu' necessario". */}
            <div style={{ marginBottom: 14, fontSize: 14, lineHeight: 1.7 }}>
              <div>
                <strong>Terminato:</strong>{' '}
                {data.terminatedAt
                  ? <>da <b>{data.terminatedByNickname ?? '—'}</b> il {fmtDateTime(data.terminatedAt)}</>
                  : <span style={{ color: '#6c757d' }}>non ancora</span>}
              </div>
              <div>
                <strong>Avviso "non più necessario":</strong>{' '}
                {data.noLongerNeededAt
                  ? <>inviato da <b>{data.noLongerNeededByNickname ?? '—'}</b> il {fmtDateTime(data.noLongerNeededAt)}</>
                  : <span style={{ color: '#6c757d' }}>non inviato</span>}
              </div>
            </div>

            {data.customGroupMembers && data.customGroupMembers.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <strong>Membri del gruppo:</strong>
                <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {data.customGroupMembers.map(m => (
                    <span key={m.userId} style={{
                      background: '#e7f3ff', color: '#0056b3', borderRadius: 4,
                      padding: '2px 8px', fontSize: 12, fontWeight: 600,
                    }}>
                      {m.nickname}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="table-wrapper" style={{ maxHeight: 420, overflowY: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>Nominativo</th>
                    <th>Disponibilita</th>
                    <th>Risposta</th>
                    <th>GPS</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recipients.map(r => (
                    <tr key={r.userId}>
                      <td>{r.nickname}</td>
                      <td>{availabilityLabel(r.availabilityState)}</td>
                      <td>
                        {r.response === 'SI' && (
                          <span style={{ color: '#28a745', fontWeight: 700 }}>SI</span>
                        )}
                        {r.response === 'NO' && (
                          <span style={{ color: '#dc3545', fontWeight: 700 }}>NO</span>
                        )}
                        {r.response === null && <span style={{ color: '#6c757d' }}>—</span>}
                      </td>
                      <td>{r.usedGps ? '📍' : ''}</td>
                    </tr>
                  ))}
                  {data.recipients.length === 0 && (
                    <tr><td colSpan={4}><div className="empty-state">Nessun destinatario</div></td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        <button onClick={onClose}
          style={{ width: '100%', padding: 12, fontSize: 16, fontWeight: 'bold',
                   border: 'none', borderRadius: 8, cursor: 'pointer',
                   background: '#6c757d', color: 'white', marginTop: 15 }}>
          Chiudi
        </button>
      </div>
    </div>
  );
}
